import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Shared bits between the Help drawer (HelpCenter) and the full-page Help
 * (/help): the dependency-free markdown renderer for the narrow article
 * spec (Docs/help-articles/README.md) and the speech-text builder for the
 * Listen button.
 */

export const renderInline = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**')
      ? <Box key={i} component="span" sx={{ fontWeight: 600 }}>{p.slice(2, -2)}</Box>
      : p
  );
};

// A "block" (blank-line separated) may still open with a heading whose body
// starts on the very next line — the common authoring style. Peel headings
// and images off line-by-line, then render what remains as list/paragraph.
const renderBlock = (block, key) => {
  const out = [];
  let lines = block.split(/\r?\n/).map((l) => l.trimEnd()).filter((l) => l.length);
  let k = 0;
  while (lines.length) {
    const line = lines[0];
    if (line.startsWith('### ')) {
      out.push(<Typography key={`${key}-h3-${k++}`} variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>{line.slice(4)}</Typography>);
      lines = lines.slice(1);
      continue;
    }
    if (line.startsWith('## ')) {
      out.push(<Typography key={`${key}-h2-${k++}`} variant="subtitle1" sx={{ fontWeight: 700, mt: 2.5, mb: 0.5 }}>{line.slice(3)}</Typography>);
      lines = lines.slice(1);
      continue;
    }
    const img = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (img) {
      out.push(
        <Box key={`${key}-img-${k++}`} component="img" src={`/help-images/${img[2].replace(/^images\//, '')}`} alt={img[1]}
          sx={{ maxWidth: '100%', borderRadius: 1, border: '1px solid', borderColor: 'divider', my: 1 }}
          onError={(e) => { e.currentTarget.style.display = 'none'; }} />
      );
      lines = lines.slice(1);
      continue;
    }
    if (/^[-*] /.test(line)) {
      const items = [];
      while (lines.length && /^[-*] /.test(lines[0])) { items.push(lines[0].replace(/^[-*] /, '')); lines = lines.slice(1); }
      out.push(
        <Box key={`${key}-ul-${k++}`} component="ul" sx={{ pl: 2.5, my: 1 }}>
          {items.map((t, j) => (
            <Typography key={j} component="li" variant="body2" sx={{ mb: 0.5, lineHeight: 1.65 }}>{renderInline(t)}</Typography>
          ))}
        </Box>
      );
      continue;
    }
    // paragraph: consume consecutive plain lines
    const para = [];
    while (lines.length && !/^(#{2,3} |[-*] |!\[)/.test(lines[0])) { para.push(lines[0]); lines = lines.slice(1); }
    out.push(
      <Typography key={`${key}-p-${k++}`} variant="body2" sx={{ my: 1, lineHeight: 1.7 }}>
        {renderInline(para.join(' '))}
      </Typography>
    );
  }
  return out;
};

export const MarkdownBody = ({ body }) => {
  const blocks = body.split(/\r?\n\r?\n/);
  return <Box>{blocks.map((block, i) => (block.trim() ? renderBlock(block.trim(), i) : null))}</Box>;
};

export const toSpeech = (article) => {
  const clean = article.body
    .replace(/^!\[.*$/gm, '')
    .replace(/^#{2,3} /gm, '')
    .replace(/\*\*/g, '')
    .replace(/^[-*] /gm, '');
  return `${article.title}. ${clean}`;
};
